import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Loader2, RefreshCw, GitMerge, X, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { metricsApiClient } from '../../api/http/wireApiClient'
import { publishingApiClient } from '../../api/http/publishingApiClient'
import type { ArticleWorkflowEntry, WorkflowHistoryEntry } from './types'
import { PIPELINE_STAGE_ORDER, PIPELINE_STAGE_COLORS } from './constants'
import { dateToApi, todayStr, nDaysAgoStr, stageLabel } from './helpers'

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseCn(dn: string): string {
  const match = /^CN=([^,]+)/i.exec(dn.trim())
  if (!match) return dn
  return match[1].replace(/\b\w/g, c => c.toUpperCase())
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600)
    const m = Math.round((seconds % 3600) / 60)
    return m > 0 ? `${h}h ${m}m` : `${h}h`
  }
  const d = Math.floor(seconds / 86400)
  const h = Math.round((seconds % 86400) / 3600)
  return h > 0 ? `${d}d ${h}h` : `${d}d`
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s
}

function sortByEditorialDesc(data: ArticleWorkflowEntry[]): ArticleWorkflowEntry[] {
  return [...data].sort((a, b) => {
    if (a.currentStage === 'blocked' && b.currentStage !== 'blocked') return -1
    if (b.currentStage === 'blocked' && a.currentStage !== 'blocked') return 1
    const aTime = a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0
    const bTime = b.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0
    return bTime - aTime
  })
}

// ── Chart theme (dark-mode-aware colors) ────────────────────────────────────────
//
// We read Radix step variables directly from the computed style because
// @theme aliased tokens (--color-fg = var(--gray-12)) aren't resolved by
// getComputedStyle — only the concrete Radix steps are.
//
// Semantic token mapping:
//   --color-fg           = --gray-12  (primary text)
//   --color-fg-secondary = --gray-11  (secondary / muted text)
//   --color-line-subtle  = --gray-6   (decorative dividers → chart gridlines)
//   --color-tooltip-bg   = --gray-12  (inverted tooltip surface)
//   --color-tooltip-fg   = --gray-1   (inverted tooltip text)

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

function resolveChartColors(): ChartColors {
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg:          get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line:        get('--gray-6')  || '#e5e7eb',
    tooltipBg:   get('--gray-12') || '#111111',
    tooltipFg:   get('--gray-1')  || '#fcfcfc',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const obs = new MutationObserver(() => setColors(resolveChartColors()))
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])
  return colors
}

// ── Timeline chart builder ──────────────────────────────────────────────────────

function buildTimelineOption(sorted: ArticleWorkflowEntry[], colors: ChartColors) {
  const yLabels = sorted.map(a => truncate(a.articleTitle, 48))
  const tooltipFormatter = (params: { seriesName: string; value: number; dataIndex: number }[]) => {
    const idx = params[0]?.dataIndex
    if (idx == null) return ''
    const article = sorted[idx]
    const lines: string[] = [`<b style="font-size:12px">${article.articleTitle}</b>`]
    for (const p of params) {
      if (p.value > 0) {
        const key = PIPELINE_STAGE_ORDER.find(s => stageLabel(s) === p.seriesName)
        const color = key ? (PIPELINE_STAGE_COLORS[key] ?? '#888') : '#888'
        lines.push(`<span style="color:${color}">●</span> ${p.seriesName}: <b>${formatDuration(p.value * 3600)}</b>`)
      }
    }
    return lines.join('<br/>')
  }
  return {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg },
      formatter: tooltipFormatter,
    },
    legend: { data: PIPELINE_STAGE_ORDER.map(stageLabel), bottom: 4, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 11, color: colors.fgSecondary } },
    grid: { top: 8, right: 80, bottom: 40, left: 8, containLabel: true },
    xAxis: {
      type: 'value' as const,
      axisLabel: { fontSize: 10, color: colors.fgSecondary, formatter: (v: number) => (v >= 24 ? `${Math.round(v / 24)}d` : `${v}h`) },
      splitLine: { lineStyle: { color: colors.line } },
    },
    yAxis: { type: 'category' as const, data: yLabels, inverse: true, triggerEvent: true, axisLabel: { fontSize: 11, color: colors.fgSecondary, width: 300, overflow: 'truncate' as const, cursor: 'pointer' } },
    series: PIPELINE_STAGE_ORDER.map(stage => ({
      name: stageLabel(stage),
      type: 'bar' as const,
      stack: 'total',
      data: sorted.map(a => { const s = a.workflowStages[stage]?.totalTimeSeconds ?? 0; return s > 0 ? +(s / 3600).toFixed(2) : 0 }),
      itemStyle: { color: PIPELINE_STAGE_COLORS[stage] },
      emphasis: { focus: 'series' as const },
    })),
  }
}

// ── Article drill-down chart builders ─────────────────────────────────────────

function buildArticleDonutOption(entry: ArticleWorkflowEntry, colors: ChartColors) {
  const stageData = PIPELINE_STAGE_ORDER
    .filter(s => entry.workflowStages[s] && entry.workflowStages[s].totalTimeSeconds > 0)
    .map(s => ({
      name: stageLabel(s),
      value: +(entry.workflowStages[s].totalTimeSeconds / 3600).toFixed(2),
      itemStyle: { color: PIPELINE_STAGE_COLORS[s] ?? '#888' },
    }))
  const totalSecs = PIPELINE_STAGE_ORDER.reduce(
    (t, s) => t + (entry.workflowStages[s]?.totalTimeSeconds ?? 0), 0,
  )
  return {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg },
      formatter: (p: { name: string; value: number; percent: number }) =>
        `<b>${p.name}</b><br/>${formatDuration(p.value * 3600)}<br/>${p.percent}%`,
    },
    graphic: {
      type: 'text',
      left: 'center',
      top: '44%',
      style: {
        text: formatDuration(totalSecs),
        textAlign: 'center',
        fill: colors.fg,
        fontSize: 13,
        fontWeight: 'bold',
      },
    },
    series: [{
      type: 'pie' as const,
      radius: ['40%', '68%'],
      center: ['50%', '50%'],
      data: stageData,
      label: {
        show: true,
        formatter: '{b}\n{d}%',
        fontSize: 10,
        lineHeight: 14,
        color: colors.fg,
        borderWidth: 0,
        backgroundColor: 'transparent',
      },
      labelLine: { length: 8, length2: 6 },
      emphasis: { scale: true, scaleSize: 4 },
    }],
  }
}

function buildArticleComparisonOption(entry: ArticleWorkflowEntry, allData: ArticleWorkflowEntry[], colors: ChartColors) {
  const stages = PIPELINE_STAGE_ORDER.filter(s =>
    allData.some(a => a.workflowStages[s] && a.workflowStages[s].totalTimeSeconds > 0)
  )
  const thisArticle = stages.map(s =>
    +((entry.workflowStages[s]?.totalTimeSeconds ?? 0) / 3600).toFixed(2)
  )
  const avgData = stages.map(s => {
    const times = allData
      .map(a => a.workflowStages[s]?.totalTimeSeconds ?? 0)
      .filter(t => t > 0)
    const avg = times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0
    return +(avg / 3600).toFixed(2)
  })
  const axisFormatter = (v: number) => v >= 24 ? `${Math.round(v / 24)}d` : `${v}h`
  return {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg },
      formatter: (params: { seriesName: string; value: number; dataIndex: number }[]) => {
        const i = params[0]?.dataIndex
        if (i == null) return ''
        const lines: string[] = [`<b>${stageLabel(stages[i])}</b>`]
        for (const p of params) {
          lines.push(`${p.seriesName}: <b>${formatDuration(p.value * 3600)}</b>`)
        }
        return lines.join('<br/>')
      },
    },
    legend: {
      data: ['This Article', 'Avg (pipeline)'],
      bottom: 0,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { fontSize: 10, color: colors.fgSecondary },
    },
    grid: { top: 8, right: 8, bottom: 44, left: 8, containLabel: true },
    xAxis: {
      type: 'category' as const,
      data: stages.map(stageLabel),
      axisLabel: { fontSize: 10, interval: 0, color: colors.fgSecondary },
    },
    yAxis: {
      type: 'value' as const,
      axisLabel: { fontSize: 9, color: colors.fgSecondary, formatter: axisFormatter },
      splitLine: { lineStyle: { color: colors.line } },
    },
    series: [
      {
        name: 'This Article',
        type: 'bar' as const,
        barMaxWidth: 36,
        data: thisArticle.map((v, i) => ({
          value: v,
          itemStyle: { color: PIPELINE_STAGE_COLORS[stages[i]] ?? '#888' },
        })),
      },
      {
        name: 'Avg (pipeline)',
        type: 'bar' as const,
        barMaxWidth: 36,
        data: avgData,
        itemStyle: { color: colors.line },
      },
    ],
  }
}

// ── KPI drill-down ────────────────────────────────────────────────────────────

type KpiKey = 'articles' | 'editorial' | 'blocked' | 'avgWait'

function buildByStageOption(data: ArticleWorkflowEntry[], colors: ChartColors) {
  const rows = PIPELINE_STAGE_ORDER
    .map(s => ({
      stage: s,
      count: data.filter(a => a.currentStage === s).length,
      avg: (() => {
        const times = data.map(a => a.workflowStages[s]?.totalTimeSeconds ?? 0).filter(t => t > 0)
        return times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0
      })(),
    }))
    .filter(r => r.count > 0)
    .sort((a, b) => b.count - a.count)
  if (rows.length === 0) return null
  return {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg },
      formatter: (params: { dataIndex: number }[]) => {
        const i = params[0]?.dataIndex
        if (i == null) return ''
        const r = rows[i]
        const lines = [`<b>${stageLabel(r.stage)}</b>`, `${r.count} article${r.count !== 1 ? 's' : ''} currently here`]
        if (r.avg > 0) lines.push(`Avg time: ${formatDuration(r.avg)}`)
        return lines.join('<br/>')
      },
    },
    grid: { top: 8, right: 60, bottom: 8, left: 8, containLabel: true },
    xAxis: { type: 'value' as const, minInterval: 1, axisLabel: { fontSize: 10, color: colors.fgSecondary }, splitLine: { lineStyle: { color: colors.line } } },
    yAxis: { type: 'category' as const, data: rows.map(r => stageLabel(r.stage)), inverse: true, axisLabel: { fontSize: 12, color: colors.fgSecondary } },
    series: [{
      type: 'bar' as const,
      data: rows.map(r => ({ value: r.count, itemStyle: { color: PIPELINE_STAGE_COLORS[r.stage] ?? '#888' } })),
      label: { show: true, position: 'right' as const, fontSize: 11, color: colors.fg },
    }],
  }
}

function buildEditorialQueueOption(data: ArticleWorkflowEntry[], colors: ChartColors) {
  const queue = data
    .filter(a => a.currentStage === 'editorial_approval')
    .map(a => ({ title: a.articleTitle, secs: a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0 }))
    .sort((a, b) => b.secs - a.secs)
  if (queue.length === 0) return null
  return {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg },
      formatter: (params: { dataIndex: number }[]) => {
        const i = params[0]?.dataIndex
        if (i == null) return ''
        return `<b>${queue[i].title}</b><br/>Waiting: <b>${formatDuration(queue[i].secs)}</b>`
      },
    },
    grid: { top: 8, right: 110, bottom: 8, left: 8, containLabel: true },
    xAxis: { type: 'value' as const, axisLabel: { fontSize: 10, color: colors.fgSecondary, formatter: (v: number) => v >= 24 ? `${Math.round(v / 24)}d` : `${v}h` }, splitLine: { lineStyle: { color: colors.line } } },
    yAxis: { type: 'category' as const, data: queue.map(a => truncate(a.title, 42)), inverse: true, axisLabel: { fontSize: 10, color: colors.fgSecondary, width: 290, overflow: 'truncate' as const } },
    series: [{
      type: 'bar' as const,
      data: queue.map(a => +(a.secs / 3600).toFixed(2)),
      itemStyle: { color: PIPELINE_STAGE_COLORS['editorial_approval'] },
      label: { show: true, position: 'right' as const, fontSize: 11, color: colors.fg, formatter: (p: { dataIndex: number }) => formatDuration(queue[p.dataIndex].secs) },
    }],
  }
}

function buildWaitDistributionOption(data: ArticleWorkflowEntry[], colors: ChartColors) {
  const articles = data
    .map(a => ({ title: a.articleTitle, secs: a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0 }))
    .filter(a => a.secs > 0)
    .sort((a, b) => b.secs - a.secs)
  if (articles.length === 0) return null
  const values = articles.map(a => a.secs)
  const mean = values.reduce((s, v) => s + v, 0) / values.length
  const sortedV = [...values].sort((a, b) => a - b)
  const median = sortedV.length % 2 === 0
    ? (sortedV[sortedV.length / 2 - 1] + sortedV[sortedV.length / 2]) / 2
    : sortedV[Math.floor(sortedV.length / 2)]
  const stddev = Math.sqrt(values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length)
  const threshold = mean + 1.5 * stddev
  return {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg },
      formatter: (params: { dataIndex: number }[]) => {
        const i = params[0]?.dataIndex
        if (i == null) return ''
        const a = articles[i]
        const lines = [`<b>${a.title}</b>`, `Editorial wait: <b>${formatDuration(a.secs)}</b>`]
        if (a.secs > threshold) lines.push(`<span style="color:#c0392b">⚠ Outlier — ${((a.secs / mean - 1) * 100).toFixed(0)}% above avg</span>`)
        return lines.join('<br/>')
      },
    },
    grid: { top: 32, right: 16, bottom: 80, left: 8, containLabel: true },
    xAxis: {
      type: 'category' as const,
      data: articles.map(a => truncate(a.title, 20)),
      axisLabel: { fontSize: 9, interval: 0, rotate: 35, color: colors.fgSecondary },
    },
    yAxis: {
      type: 'value' as const,
      axisLabel: { fontSize: 10, color: colors.fgSecondary, formatter: (v: number) => v >= 24 ? `${Math.round(v / 24)}d` : `${v}h` },
      splitLine: { lineStyle: { color: colors.line } },
    },
    series: [{
      type: 'bar' as const,
      data: articles.map(a => ({
        value: +(a.secs / 3600).toFixed(2),
        itemStyle: { color: a.secs > threshold ? '#c0392b' : PIPELINE_STAGE_COLORS['editorial_approval'] },
      })),
      markLine: {
        symbol: 'none',
        silent: true,
        data: [
          {
            yAxis: +(mean / 3600).toFixed(2),
            lineStyle: { color: '#d97706', type: 'dashed' as const, width: 1.5 },
            label: { formatter: `Avg ${formatDuration(mean)}`, fontSize: 10, color: '#d97706', position: 'insideEndTop' as const },
          },
          {
            yAxis: +(median / 3600).toFixed(2),
            lineStyle: { color: '#9ca3af', type: 'dotted' as const, width: 1.5 },
            label: { formatter: `Median ${formatDuration(median)}`, fontSize: 10, color: '#9ca3af', position: 'insideEndBottom' as const },
          },
        ],
      },
    }],
  }
}

function buildBlockedOption(data: ArticleWorkflowEntry[], colors: ChartColors) {
  const blocked = data
    .filter(a => a.currentStage === 'blocked')
    .map(a => ({ title: a.articleTitle, secs: a.workflowStages['blocked']?.totalTimeSeconds ?? 0 }))
    .sort((a, b) => b.secs - a.secs)
  if (blocked.length === 0) return null
  return {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg },
      formatter: (params: { dataIndex: number }[]) => {
        const i = params[0]?.dataIndex
        if (i == null) return ''
        return `<b>${blocked[i].title}</b><br/>Blocked for: <b>${formatDuration(blocked[i].secs)}</b>`
      },
    },
    grid: { top: 8, right: 110, bottom: 8, left: 8, containLabel: true },
    xAxis: { type: 'value' as const, axisLabel: { fontSize: 10, color: colors.fgSecondary, formatter: (v: number) => v >= 24 ? `${Math.round(v / 24)}d` : `${v}h` }, splitLine: { lineStyle: { color: colors.line } } },
    yAxis: { type: 'category' as const, data: blocked.map(a => truncate(a.title, 42)), inverse: true, axisLabel: { fontSize: 10, color: colors.fgSecondary, width: 290, overflow: 'truncate' as const } },
    series: [{
      type: 'bar' as const,
      data: blocked.map(a => +(a.secs / 3600).toFixed(2)),
      itemStyle: { color: PIPELINE_STAGE_COLORS['blocked'] },
      label: { show: true, position: 'right' as const, fontSize: 11, color: colors.fg, formatter: (p: { dataIndex: number }) => formatDuration(blocked[p.dataIndex].secs) },
    }],
  }
}

function KpiDrillDown({
  kpiKey,
  data,
  onClose,
}: {
  kpiKey: KpiKey
  data: ArticleWorkflowEntry[]
  onClose: () => void
}) {
  const colors = useChartTheme()
  const chartOpt = useMemo(() => {
    switch (kpiKey) {
      case 'articles':  return buildByStageOption(data, colors)
      case 'editorial': return buildEditorialQueueOption(data, colors)
      case 'blocked':   return buildBlockedOption(data, colors)
      case 'avgWait':   return buildWaitDistributionOption(data, colors)
    }
  }, [kpiKey, data, colors])

  const chartHeight = useMemo(() => {
    switch (kpiKey) {
      case 'articles': {
        const n = PIPELINE_STAGE_ORDER.filter(s => data.some(a => a.currentStage === s)).length
        return Math.max(200, n * 60 + 60)
      }
      case 'editorial': {
        const n = data.filter(a => a.currentStage === 'editorial_approval').length
        return Math.max(200, n * 52 + 60)
      }
      case 'blocked': {
        const n = data.filter(a => a.currentStage === 'blocked').length
        return n === 0 ? 200 : Math.max(200, n * 60 + 60)
      }
      case 'avgWait': {
        const n = data.filter(a => (a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0) > 0).length
        return Math.max(260, n * 44 + 120)
      }
    }
  }, [kpiKey, data])

  const titleMap: Record<KpiKey, string> = {
    articles:  'Articles by Current Stage',
    editorial: 'Editorial Approval Queue',
    blocked:   'Blocked Articles',
    avgWait:   'Editorial Wait Distribution',
  }

  // Stats for right panel
  const editorialTimes = data.map(a => a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0).filter(v => v > 0)
  const mean = editorialTimes.length ? editorialTimes.reduce((s, v) => s + v, 0) / editorialTimes.length : 0
  const sortedT = [...editorialTimes].sort((a, b) => a - b)
  const median = sortedT.length
    ? sortedT.length % 2 === 0
      ? (sortedT[sortedT.length / 2 - 1] + sortedT[sortedT.length / 2]) / 2
      : sortedT[Math.floor(sortedT.length / 2)]
    : 0
  const stddev = editorialTimes.length
    ? Math.sqrt(editorialTimes.reduce((s, v) => s + (v - mean) ** 2, 0) / editorialTimes.length)
    : 0
  const outlierThreshold = mean + 1.5 * stddev
  const outliers = data.filter(a => (a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0) > outlierThreshold)
  const nonOutlierTimes = editorialTimes.filter(v => v <= outlierThreshold)
  const meanWithout = nonOutlierTimes.length ? nonOutlierTimes.reduce((s, v) => s + v, 0) / nonOutlierTimes.length : mean
  const stageRows = PIPELINE_STAGE_ORDER
    .map(s => ({
      stage: s,
      current: data.filter(a => a.currentStage === s).length,
      avg: (() => {
        const times = data.map(a => a.workflowStages[s]?.totalTimeSeconds ?? 0).filter(t => t > 0)
        return times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0
      })(),
    }))
    .filter(r => r.current > 0)
  const editorialQueue = data
    .filter(a => a.currentStage === 'editorial_approval')
    .sort((a, b) =>
      (b.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0) -
      (a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0)
    )
  const blockedArticles = data
    .filter(a => a.currentStage === 'blocked')
    .sort((a, b) =>
      (b.workflowStages['blocked']?.totalTimeSeconds ?? 0) -
      (a.workflowStages['blocked']?.totalTimeSeconds ?? 0)
    )

  return (
    <div
      className="flex overflow-hidden border-y border-line bg-surface"
      style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }}
    >
      {/* Left — chart */}
      <div className="w-1/2 overflow-y-auto border-r border-line p-6" style={{ maxHeight: '82vh' }}>
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <GitMerge size={14} className="text-fg-secondary" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">{titleMap[kpiKey]}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1 rounded border border-line px-2.5 py-1 font-sans text-[11px] font-semibold text-fg-secondary hover:bg-fill hover:text-fg"
          >
            <X size={11} />
            Close
          </button>
        </div>
        {chartOpt ? (
          <ReactECharts option={chartOpt} style={{ height: chartHeight }} />
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <CheckCircle2 size={28} className="mb-3 text-green-500" />
            <p className="font-sans text-sm font-semibold text-fg">Nothing to show here</p>
          </div>
        )}
      </div>

      {/* Right — stats */}
      <div className="w-1/2 overflow-y-auto p-6" style={{ maxHeight: '82vh' }}>
        {kpiKey === 'avgWait' && (
          <div className="space-y-6">
            <div>
              <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Statistics</p>
              <div className="grid grid-cols-3 gap-3">
                {([['Mean', mean], ['Median', median], ['Std Dev', stddev]] as [string, number][]).map(([label, val]) => (
                  <div key={label} className="rounded border border-line bg-canvas p-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">{label}</p>
                    <p className="mt-1 text-xl font-bold text-fg">{formatDuration(val)}</p>
                  </div>
                ))}
              </div>
            </div>
            {outliers.length > 0 ? (
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                  Outlier Impact — {outliers.length} article{outliers.length !== 1 ? 's' : ''}
                </p>
                <p className="mb-3 font-sans text-[12px] text-fg-secondary">
                  Removing outliers drops the avg from{' '}
                  <span className="font-bold text-fg">{formatDuration(mean)}</span> to{' '}
                  <span className="font-bold text-fg">{formatDuration(meanWithout)}</span>
                  {' '}— <span className="font-bold text-red-600">{Math.round((mean / meanWithout - 1) * 100)}% inflation</span>.
                </p>
                <div className="divide-y divide-line-subtle rounded border border-red-200">
                  {outliers
                    .sort((a, b) => (b.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0) - (a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0))
                    .map(a => {
                      const secs = a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0
                      return (
                        <div key={a.articleId} className="flex items-center justify-between gap-3 px-3 py-2.5">
                          <p className="min-w-0 truncate font-sans text-xs text-fg">{a.articleTitle}</p>
                          <div className="shrink-0 text-right">
                            <span className="font-sans text-xs font-bold text-red-600">{formatDuration(secs)}</span>
                            <span className="ml-1.5 font-sans text-[10px] text-fg-secondary">+{Math.round((secs / mean - 1) * 100)}% vs avg</span>
                          </div>
                        </div>
                      )
                    })}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded border border-line bg-canvas px-4 py-3">
                <CheckCircle2 size={16} className="shrink-0 text-green-500" />
                <p className="font-sans text-sm text-fg-secondary">No statistical outliers detected.</p>
              </div>
            )}
          </div>
        )}

        {kpiKey === 'articles' && (
          <div className="space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Current Stage Distribution</p>
            {stageRows.length > 0 ? (
              <div className="divide-y divide-line-subtle rounded border border-line">
                {stageRows.map(r => (
                  <div key={r.stage} className="flex items-center gap-3 px-4 py-3">
                    <div className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: PIPELINE_STAGE_COLORS[r.stage] ?? '#888' }} />
                    <p className="flex-1 font-sans text-sm text-fg">{stageLabel(r.stage)}</p>
                    <span className="font-sans text-sm font-bold text-fg">{r.current}</span>
                    {r.avg > 0 && <span className="font-sans text-[11px] text-fg-secondary">avg {formatDuration(r.avg)}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-sans text-sm text-fg-secondary">No articles in tracked stages.</p>
            )}
          </div>
        )}

        {kpiKey === 'editorial' && (
          editorialQueue.length === 0 ? (
            <div className="flex items-center gap-2 rounded border border-line bg-canvas px-4 py-3">
              <CheckCircle2 size={16} className="shrink-0 text-green-500" />
              <p className="font-sans text-sm text-fg-secondary">No articles currently in editorial approval.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                {editorialQueue.length} article{editorialQueue.length !== 1 ? 's' : ''} waiting
              </p>
              <div className="divide-y divide-line-subtle rounded border border-line">
                {editorialQueue.map(a => {
                  const secs = a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0
                  const editors = (a.workflowStages['editorial_approval']?.touchedClients ?? []).map(parseCn)
                  return (
                    <div key={a.articleId} className="px-4 py-3">
                      <p className="font-sans text-xs font-semibold text-fg">{a.articleTitle}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="font-sans text-[11px] font-bold" style={{ color: PIPELINE_STAGE_COLORS['editorial_approval'] }}>{formatDuration(secs)}</span>
                        {editors.length > 0 && <span className="font-sans text-[11px] text-fg-secondary">→ {editors.join(', ')}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        )}

        {kpiKey === 'blocked' && (
          blockedArticles.length === 0 ? (
            <div className="flex items-center gap-2 rounded border border-line bg-canvas px-4 py-3">
              <CheckCircle2 size={16} className="shrink-0 text-green-500" />
              <p className="font-sans text-sm text-fg-secondary">No blocked articles in this window.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                {blockedArticles.length} blocked article{blockedArticles.length !== 1 ? 's' : ''}
              </p>
              <div className="divide-y divide-line-subtle rounded border border-red-200">
                {blockedArticles.map(a => {
                  const secs = a.workflowStages['blocked']?.totalTimeSeconds ?? 0
                  const editors = (a.workflowStages['blocked']?.touchedClients ?? []).map(parseCn)
                  return (
                    <div key={a.articleId} className="px-4 py-3">
                      <p className="font-sans text-xs font-semibold text-fg">{a.articleTitle}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="font-sans text-[11px] font-bold text-red-600">{formatDuration(secs)}</span>
                        {editors.length > 0 && <span className="font-sans text-[11px] text-fg-secondary">last: {editors.join(', ')}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}

// ── ArticleHistoryTimeline ───────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  )
}

function formatReason(reason: string): string {
  return reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function ArticleHistoryTimeline({ entry }: { entry: ArticleWorkflowEntry }) {
  const [history, setHistory] = useState<WorkflowHistoryEntry[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    publishingApiClient
      .get<WorkflowHistoryEntry[]>(`/drafts/${entry.articleId}/workflow-history`)
      .then(res => setHistory(res.data.slice().reverse())) // API returns newest-first; reverse to oldest-first
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load history'))
      .finally(() => setLoading(false))
  }, [entry.articleId])

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-fg-secondary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-6 py-5">
        <div className="rounded border border-error-line bg-error-fill px-3 py-2">
          <p className="font-sans text-sm text-error-fg">{error}</p>
        </div>
      </div>
    )
  }

  if (!history || history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="font-sans text-sm text-fg-secondary">No transition history found.</p>
      </div>
    )
  }

  const now = Date.now()

  return (
    <div className="divide-y divide-line-subtle">
      {history.map((event, idx) => {
        const color = PIPELINE_STAGE_COLORS[event.stage] ?? '#888'
        const isCurrent = idx === history.length - 1
        const enteredMs = new Date(event.changedAt).getTime()
        const leftMs = idx < history.length - 1
          ? new Date(history[idx + 1].changedAt).getTime()
          : now
        const durationSecs = Math.max(0, Math.round((leftMs - enteredMs) / 1000))
        const isRework = idx > 0 && history.slice(0, idx).some(e => e.stage === event.stage)
        const displayName = event.changedByName ?? parseCn(event.changedBy)

        return (
          <div key={event.id} className="px-5 py-4">
            <div className="flex items-start gap-3">
              {/* Stage color dot — with pulse on current */}
              <div className="relative mt-1 shrink-0">
                {isCurrent && (
                  <span
                    className="absolute inset-0 animate-ping rounded-full opacity-50"
                    style={{ backgroundColor: color }}
                  />
                )}
                <div className="relative h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
              </div>

              {/* Row content */}
              <div className="min-w-0 flex-1 space-y-1">
                {/* Stage name + badges */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-sans text-sm font-bold" style={{ color }}>
                    {stageLabel(event.stage)}
                  </span>
                  {isRework && (
                    <span className="rounded-full border border-amber-400 bg-amber-50 px-1.5 py-px font-sans text-[9px] font-bold text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                      ↩ rework
                    </span>
                  )}
                  {isCurrent && (
                    <span className="rounded-full bg-fill px-1.5 py-px font-sans text-[9px] font-semibold text-fg-secondary">
                      current
                    </span>
                  )}
                </div>

                {/* Timestamp + who */}
                <p className="font-sans text-[11px] text-fg-secondary">
                  {formatDateTime(event.changedAt)}
                  <span className="mx-1 opacity-40">·</span>
                  moved by <span className="font-medium text-fg">{displayName}</span>
                </p>

                {/* Reason */}
                {event.reason && (
                  <p className="font-sans text-[11px] text-fg-secondary">
                    Reason:{' '}
                    <span className="rounded bg-fill px-1.5 py-0.5 font-mono text-[10px] font-medium text-fg">
                      {formatReason(event.reason)}
                    </span>
                  </p>
                )}

                {/* Notes */}
                {event.notes && (
                  <p className="rounded border border-line-subtle bg-surface px-2.5 py-1.5 font-sans text-[11px] italic text-fg">
                    "{event.notes}"
                  </p>
                )}

                {/* Duration in this stage */}
                <p className="font-sans text-[11px]" style={{ color }}>
                  {isCurrent
                    ? <><b>{formatDuration(durationSecs)}</b> in this stage so far</>
                    : <><b>{formatDuration(durationSecs)}</b> in this stage</>
                  }
                </p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── ArticleDetailPanel ────────────────────────────────────────────────────────

function ArticleDetailPanel({
  entry,
  allData,
  onClose,
}: {
  entry: ArticleWorkflowEntry
  allData: ArticleWorkflowEntry[]
  onClose: () => void
}) {
  const [activeTab, setActiveTab] = useState<'distribution' | 'journey'>('distribution')
  const controls = entry.disseminationControls ?? []
  const colors = useChartTheme()
  const donutOpt = useMemo(() => buildArticleDonutOption(entry, colors), [entry, colors])
  const compOpt = useMemo(() => buildArticleComparisonOption(entry, allData, colors), [entry, allData, colors])

  return (
    <div className="rounded border border-line bg-surface overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <p className="font-sans text-sm font-semibold text-fg leading-snug">{entry.articleTitle}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {entry.productType && (
              <span className="rounded border border-line px-1.5 py-0.5 font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                {entry.productType}
              </span>
            )}
            <span className="rounded border border-line px-1.5 py-0.5 font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
              {entry.classification}
            </span>
            {controls.map(ctrl => (
              <span
                key={ctrl}
                className="rounded border border-line px-1.5 py-0.5 font-sans text-[10px] font-bold uppercase tracking-widest text-amber-600"
              >
                {ctrl}
              </span>
            ))}
            <span
              className="ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-sans text-[11px] font-semibold"
              style={{
                backgroundColor:
                  (PIPELINE_STAGE_COLORS[entry.currentStage] ?? '#888') + '22',
                color: PIPELINE_STAGE_COLORS[entry.currentStage] ?? '#888',
              }}
            >
              {entry.currentStage === 'blocked' && <AlertTriangle size={10} />}
              {stageLabel(entry.currentStage)}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg"
        >
          <X size={14} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-line">
        {(['distribution', 'journey'] as const).map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 font-sans text-[11px] font-bold uppercase tracking-widest transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-fg text-fg'
                : 'text-fg-secondary hover:text-fg'
            }`}
          >
            {tab === 'distribution' ? 'Time Distribution' : 'Stage Journey'}
          </button>
        ))}
      </div>

      {activeTab === 'distribution' ? (
        <>
          {/* Charts */}
          <div className="flex flex-col gap-4 border-b border-line px-6 py-5">
            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Time Distribution</p>
              <ReactECharts option={donutOpt} style={{ height: 220 }} />
            </div>
            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">vs. Pipeline Average</p>
              <ReactECharts option={compOpt} style={{ height: 220 }} />
            </div>
          </div>

          {/* Stage breakdown */}
          <div className="divide-y divide-line-subtle">
            {PIPELINE_STAGE_ORDER.filter(s => entry.workflowStages[s]).map(stage => {
              const metrics = entry.workflowStages[stage]
              const stageColor = PIPELINE_STAGE_COLORS[stage] ?? '#888'
              const isRework = metrics.stageEntryCount > 1
              const clients = metrics.touchedClients.map(parseCn)

              return (
                <div key={stage} className="flex items-start gap-4 px-5 py-3.5">
                  {/* Color swatch */}
                  <div
                    className="mt-0.5 h-3 w-3 shrink-0 rounded-sm"
                    style={{ backgroundColor: stageColor }}
                  />

                  {/* Stage info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-sans text-sm font-semibold text-fg">
                        {stageLabel(stage)}
                      </span>
                      <span
                        className="font-sans text-sm font-bold"
                        style={{ color: stageColor }}
                      >
                        {formatDuration(metrics.totalTimeSeconds)}
                      </span>
                      {isRework && (
                        <span className="rounded-full border border-amber-400 px-1.5 py-0.5 font-sans text-[10px] font-bold text-amber-600">
                          ↩ rework ×{metrics.stageEntryCount}
                        </span>
                      )}
                    </div>
                    {clients.length > 0 && (
                      <p className="mt-0.5 font-sans text-[11px] text-fg-secondary">
                        Touched by: {clients.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <ArticleHistoryTimeline entry={entry} />
      )}
    </div>
  )
}

// ── PipelineDashboard ─────────────────────────────────────────────────────────

export function PipelineDashboard() {
  const [from, setFrom] = useState(nDaysAgoStr(90))
  const [to, setTo] = useState(todayStr)
  const [data, setData] = useState<ArticleWorkflowEntry[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [selectedKpi, setSelectedKpi] = useState<KpiKey | null>(null)
  const [filterText, setFilterText] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    setSelectedIndex(null)
    setSelectedKpi(null)
    metricsApiClient
      .get<ArticleWorkflowEntry[]>('/metrics/article-workflows', {
        params: { from: dateToApi(from, false), to: dateToApi(to, true) },
      })
      .then(res => setData(res.data))
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }, [from, to])

  const sortedData = useMemo(() => (data ? sortByEditorialDesc(data) : []), [data])

  const filteredData = useMemo(() => {
    const q = filterText.trim().toLowerCase()
    return q ? sortedData.filter(a => a.articleTitle.toLowerCase().includes(q)) : sortedData
  }, [sortedData, filterText])

  const kpis = useMemo(() => {
    if (!data) return []
    const inEditorial = data.filter(a => a.currentStage === 'editorial_approval').length
    const blocked = data.filter(a => a.currentStage === 'blocked').length
    const editorialTimes = data.map(a => a.workflowStages['editorial_approval']?.totalTimeSeconds ?? 0).filter(t => t > 0)
    const avgDays = editorialTimes.length > 0
      ? (editorialTimes.reduce((s, t) => s + t, 0) / editorialTimes.length / 86400).toFixed(1)
      : null
    return [
      { key: 'articles' as KpiKey,  label: 'Articles in Pipeline',  value: String(data.length)          },
      { key: 'editorial' as KpiKey, label: 'In Editorial Approval', value: String(inEditorial)           },
      { key: 'blocked' as KpiKey,   label: 'Blocked',               value: String(blocked), alert: blocked > 0 },
      { key: 'avgWait' as KpiKey,   label: 'Avg Editorial Wait',    value: avgDays ? `${avgDays}d` : '—' },
    ]
  }, [data])

  const handleBarClick = useCallback((params: { dataIndex: number; componentType?: string }) => {
    if (params.componentType !== 'series' && params.componentType !== 'yAxis') return
    setSelectedIndex(prev => (prev === params.dataIndex ? null : params.dataIndex))
  }, [])

  // Reset selection when the filtered list changes (indexes shift)
  useEffect(() => { setSelectedIndex(null) }, [filteredData])

  const chartColors = useChartTheme()
  const chartOption = useMemo(
    () => (filteredData.length ? buildTimelineOption(filteredData, chartColors) : null),
    [filteredData, chartColors],
  )

  const chartHeight = useMemo(
    () => Math.max(320, filteredData.length * 44 + 80),
    [filteredData],
  )

  const selectedArticle = selectedIndex != null ? filteredData[selectedIndex] ?? null : null

  return (
    <div className="space-y-6">

      {/* Controls bar */}
      <div className="flex flex-wrap items-end gap-3 rounded border border-line bg-surface px-5 py-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">From</label>
          <input
            type="date" value={from} max={to}
            onChange={e => setFrom(e.target.value)}
            className="rounded border border-line bg-canvas px-3 py-1.5 font-sans text-sm text-fg focus:border-line-strong focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">To</label>
          <input
            type="date" value={to} min={from} max={todayStr()}
            onChange={e => setTo(e.target.value)}
            className="rounded border border-line bg-canvas px-3 py-1.5 font-sans text-sm text-fg focus:border-line-strong focus:outline-none"
          />
        </div>
        <button
          type="button" onClick={load} disabled={loading}
          className="flex items-center gap-2 rounded border border-line bg-fg px-5 py-1.5 font-sans text-sm font-semibold text-canvas hover:opacity-90 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {loading ? 'Loading…' : 'Load'}
        </button>

        {data && (
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Filter</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Article title…"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                className="w-48 rounded border border-line bg-canvas px-3 py-1.5 font-sans text-sm text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none"
              />
              {filterText && (
                <button
                  type="button"
                  onClick={() => setFilterText('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-secondary hover:text-fg"
                  aria-label="Clear filter"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
        )}

        {data && (
          <span className="font-sans text-[11px] text-fg-secondary">
            {filterText
              ? `${filteredData.length} of ${data.length} article${data.length !== 1 ? 's' : ''}`
              : `${data.length} article${data.length !== 1 ? 's' : ''}`
            }
          </span>
        )}
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 font-sans text-sm text-red-700">{error}</div>
      )}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {kpis.map(({ key, label, value, alert }) => (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedKpi(prev => prev === key ? null : key)}
                className={`rounded border p-4 text-left transition-colors hover:bg-fill/60 ${
                  selectedKpi === key ? 'border-fg-secondary bg-fill' : 'border-line bg-surface'
                }`}
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">{label}</p>
                <p className={`mt-1.5 text-3xl font-bold ${alert ? 'text-red-600' : 'text-fg'}`}>{value}</p>
              </button>
            ))}
          </div>

          {/* KPI drill-down */}
          {selectedKpi && (
            <KpiDrillDown kpiKey={selectedKpi} data={data} onClose={() => setSelectedKpi(null)} />
          )}

          {/* Timeline chart + article drill-down */}
          {!selectedKpi && selectedArticle && chartOption ? (
            <div
              className="flex overflow-hidden border-y border-line bg-surface"
              style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }}
            >
              <div className="w-1/2 overflow-y-auto border-r border-line p-6" style={{ maxHeight: '82vh' }}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <GitMerge size={14} className="text-fg-secondary" />
                    <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Pipeline Intelligence</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedIndex(null)}
                    className="flex items-center gap-1 rounded border border-line px-2.5 py-1 font-sans text-[11px] font-semibold text-fg-secondary hover:bg-fill hover:text-fg"
                  >
                    <X size={11} />
                    Overview
                  </button>
                </div>
                <p className="mb-4 text-[11px] text-fg-secondary">Cumulative stage time per article, sorted by editorial wait · Click a bar to inspect</p>
                <ReactECharts option={chartOption} style={{ height: chartHeight }} onEvents={{ click: handleBarClick }} />
              </div>
              <div className="w-1/2 overflow-y-auto" style={{ maxHeight: '82vh' }}>
                <ArticleDetailPanel entry={selectedArticle} allData={data!} onClose={() => setSelectedIndex(null)} />
              </div>
            </div>
          ) : !selectedKpi && chartOption ? (
            <div className="rounded border border-line bg-surface p-6">
              <div className="mb-1 flex items-center gap-2">
                <GitMerge size={14} className="text-fg-secondary" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Pipeline Intelligence</p>
              </div>
              <p className="mb-4 text-[11px] text-fg-secondary">Cumulative stage time per article, sorted by editorial wait · Click a bar to inspect</p>
              <ReactECharts option={chartOption} style={{ height: chartHeight }} onEvents={{ click: handleBarClick }} />
            </div>
          ) : null}
        </>
      )}

      {!data && !loading && !error && (
        <div className="flex flex-col items-center justify-center rounded border border-dashed border-line bg-surface py-20">
          <GitMerge size={28} className="mb-3 text-fg-secondary" />
          <p className="font-sans text-sm font-semibold text-fg">Select a date range and click Load</p>
          <p className="mt-1 font-sans text-[12px] text-fg-secondary">
            Per-article stage timing and bottleneck analysis will appear here.
          </p>
        </div>
      )}
    </div>
  )
}
